/**
 * DASHBOARD TABS — the Detailed View's IN-PAGE view switcher (Metrics / Deep
 * Dive / Glossary), rendered as a segmented pill control (spec
 * navigation-model): visually lighter than, and a different shape from, the
 * page-level tab strip above it, so the two levels never read as one tab bar.
 *
 * Semantics: role="group" of toggle buttons (aria-pressed), NOT a
 * role="tablist" — the WAI-ARIA tabs pattern also requires arrow-key roving
 * focus, tabindex management, and aria-controls referencing a role="tabpanel"
 * on the associated content; the tab panels are rendered by a separate page
 * component (ExecutiveSummaryPage), so wearing role="tab" without wiring the
 * rest would be an a11y regression from the Cloudscape control we replaced.
 * A toggle-button group IS the correct pattern for a segmented control and
 * gives keyboard operability out of the box (Tab-focus + Enter/Space).
 *
 * State stays with the caller (ExecutiveSummaryPage owns the `?tab=` URL
 * sync); this component is presentation only.
 */

import React from 'react';
import { WbrViewTab } from 'src/types';

interface DashboardTabsProps {
  tabs: WbrViewTab[];
  activeTabId: string;
  onTabChange: (tabId: string) => void;
}

export const DashboardTabs: React.FC<DashboardTabsProps> = ({
  tabs,
  activeTabId,
  onTabChange,
}) => {
  return (
    <div className="view-segments" role="group" aria-label="Views">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          aria-pressed={tab.id === activeTabId}
          onClick={() => onTabChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};
